import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { Subscription } from 'rxjs';
import { LanguageService } from 'src/app/core/services/language.service';
import { TimePeriod } from 'src/app/shared/dashboard/models/time-period.enum';
import { UiSelectOptions } from 'src/app/shared/models/ui-select-option.model';
import { UiDateRangeValue } from 'src/app/shared/ui-elements/ui-date-range-picker/ui-date-range-picker.component';
import { DashboardCustomRangeDialogComponent } from '../dashboard-custom-range-dialog/dashboard-custom-range-dialog.component';

@Component({
  selector: 'app-dashboard-time-period-select',
  templateUrl: './dashboard-time-period-select.component.html',
  styleUrls: ['./dashboard-time-period-select.component.scss'],
  standalone: false,
})
export class DashboardTimePeriodSelectComponent implements OnInit, OnDestroy {
  @Input() periodControl: FormControl<TimePeriod | null>;
  @Input() customRangeControl: FormControl<UiDateRangeValue | null>;
  @Input() periodOptions: UiSelectOptions = [
    { id: TimePeriod.AllTime, name: 'allTime' },
    { id: TimePeriod.Last7Days, name: 'last7Days' },
    { id: TimePeriod.Last30Days, name: 'last30Days' },
    { id: TimePeriod.Last90Days, name: 'last90Days' },
    { id: TimePeriod.YearToDate, name: 'yearToDate' },
    { id: TimePeriod.Custom, name: 'custom', icon: 'calendar_today' },
  ];

  customRangeDisplayText = '';

  private lastPresetPeriod = TimePeriod.Last30Days;
  private readonly subscription = new Subscription();

  constructor(
    private dialog: MatDialog,
    private languageService: LanguageService
  ) {}

  ngOnInit(): void {
    if (this.periodControl.value && this.periodControl.value !== TimePeriod.Custom) {
      this.lastPresetPeriod = this.periodControl.value;
    }

    this.updateRangeDisplayText(this.customRangeControl.value);
    this.subscription.add(
      this.customRangeControl.valueChanges.subscribe((range) => {
        this.updateRangeDisplayText(range);
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  onPeriodChange(event: MatSelectChange): void {
    const period = event.value as TimePeriod;
    if (period === TimePeriod.Custom) {
      return;
    }

    this.lastPresetPeriod = period;
  }

  onOptionClick(optionId: string | number): void {
    if (optionId !== TimePeriod.Custom) {
      return;
    }

    this.openCustomRangeDialog();
  }

  private openCustomRangeDialog(): void {
    const hadCustomRange = !!this.customRangeControl.value;
    const dialogRef = this.dialog.open(DashboardCustomRangeDialogComponent, {
      data: {
        range: this.customRangeControl.value,
        maxDate: new Date(),
      },
      width: '20rem',
      maxWidth: 'calc(100vw - 2rem)',
      panelClass: 'dashboard-custom-range-dialog',
    });

    this.subscription.add(
      dialogRef.afterClosed().subscribe((range: UiDateRangeValue | undefined) => {
        if (range) {
          this.customRangeControl.setValue(range);
          return;
        }

        if (hadCustomRange) {
          this.periodControl.setValue(TimePeriod.Custom, { emitEvent: false });
          return;
        }

        this.periodControl.setValue(this.lastPresetPeriod, { emitEvent: false });
      })
    );
  }

  private updateRangeDisplayText(range: UiDateRangeValue | null): void {
    this.customRangeDisplayText = range ? this.formatRangeLabel(range) : '';
  }

  private formatRangeLabel(range: UiDateRangeValue): string {
    const locale = this.languageService.getLocaleForLanguage(
      this.languageService.getCurrentLanguage()
    );
    const formatter = new Intl.DateTimeFormat(locale, {
      month: 'short',
      day: '2-digit',
    });
    return `${formatter.format(range.from)} – ${formatter.format(range.to)}`;
  }
}
